
import hanson from 'hanson';
import GeoJSON from 'ol/format/GeoJSON';
import Interaction from 'ol/interaction/Interaction';
import ImageWMSSource from 'ol/source/ImageWMS';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import TileWMSSource from 'ol/source/TileWMS';
import WMSGetFeatureInfo from 'ol/format/WMSGetFeatureInfo';

import { getLayerById, getLayerIdFromFeature } from './olUtils';

class Info extends Interaction {

    constructor(opt_options) {
        const options = opt_options ? opt_options : {};
        super(options);

        this._highlightFeatures = [];

        if (options.handleClickEvent) {
            this.handleClickEvent = options.handleClickEvent;
        }
    }

    handleClickEvent(mapBrowserEvent) {
        return false;
    }

    handlePointerMove(mapBrowserEvent) {
        if (mapBrowserEvent.dragging) {
            return;
        }

        let features = getFeaturesFromEvent(mapBrowserEvent.map, mapBrowserEvent);

        this._highlightFeatures.forEach(item => {
            item.setStyle(null);
        });
        this._highlightFeatures = [];

        features.forEach(feature => {
            let layer = getLayerById(mapBrowserEvent.map, getLayerIdFromFeature(feature));
            if (layer) {
                let highlightStyle = layer.get('highlightStyle');
                if (highlightStyle) {
                    feature.setStyle(highlightStyle);
                    this._highlightFeatures.push(feature);
                }
            }
        });
    }

    handleEvent(mapBrowserEvent) {
        let stopEvent = false;
        if (mapBrowserEvent.type == MapBrowserEventType.CLICK) {
            const browserEvent = mapBrowserEvent.originalEvent;
            this.handleClickEvent(mapBrowserEvent);
            browserEvent.preventDefault();
            stopEvent = true;
        }
        if (mapBrowserEvent.type == MapBrowserEventType.POINTERMOVE) {
            const browserEvent = mapBrowserEvent.originalEvent;
            this.handlePointerMove(mapBrowserEvent);
        }
        return !stopEvent;
    }
}

function getFeaturesAtPixel(map, pixel) {
    let features = [];
    map.forEachFeatureAtPixel(pixel, (feature) => {
        let canUseFeature = false;
        if (feature && feature.layerId) {
            canUseFeature = true;
            // ToDo: ignoring layers by id defined in string is not ideal
            const internalLayerNames = ['mapmultiscene_look', '_HIGHLIGHT_'];
            if (internalLayerNames.indexOf(feature.layerId) !== -1) {
                canUseFeature = false;
            }
        }
        if (canUseFeature) {
            features.push(feature);
        }
    });
    return features;
}

function getFeaturesFromEvent(map, evt) {
    let pixel = map.getEventPixel(evt.originalEvent);
    let features = getFeaturesAtPixel(map, pixel);
    return features;
}

async function getFeaturesFromWMS(view, wmsDataSource, coord, layerId) {
    let result = [];
    let dataSourceParams = wmsDataSource.getParams();
    if (dataSourceParams.info || dataSourceParams.attachedDataModel) {
        const infoFormat = dataSourceParams.infoFormat || 'application/vnd.ogc.gml';
        const url = wmsDataSource.getFeatureInfoUrl(
            coord,
            view.getResolution(),
            view.getProjection(),
            {
                'INFO_FORMAT': infoFormat,
                'QUERY_LAYERS': dataSourceParams['layers'],
                'FEATURE_COUNT': 100,
            }
        );
        if (url) {
            await fetch(url)
                .then((response) => response.text())
                .then((content) => {
                    const features = getFeaturesFromInfoContent(content, infoFormat);
                    features.forEach(item => {
                        item.layerId = layerId;
                    });
                    result = [
                        ...features
                    ];
                });
        }
    }
    return result;
}

function getFeaturesFromInfoContent(content, infoFormat) {
    switch (infoFormat) {
        case 'application/json':
            try {
                let format = new GeoJSON();
                const json = hanson.parse(content);
                if (json && json.length > 0) {
                    return format.readFeatures(json[0]);
                } else {
                    return [];
                }
            }
            catch(e) {
                return [];
            }
        case 'application/vnd.ogc.gml':
            return new WMSGetFeatureInfo().readFeatures(content);
        default:
            return [];
    }
}

async function processClick(mapBrowserEvent, options) {
    let processed = false;
    let map = mapBrowserEvent.map;
    let features = getFeaturesFromEvent(mapBrowserEvent.map, mapBrowserEvent);

    if (options.onFeatureClick) {
        if (features && (features.length > 0)) {
            options.onFeatureClick(features[0]);
            processed = true;
        }

        if (!processed) {
            let layers = [];
            map.getLayers().forEach(item => {
                if (item.getVisible()) {
                    let source = item.getSource();
                    if ((source instanceof TileWMSSource) || (source instanceof ImageWMSSource)) {
                        layers.push(item);
                    }
                }
            });

            if (options.onShowMessage && (layers.length > 1)) {
                options.onShowMessage('Vyhledávám objekty v mapě...');
            }

            let allFeatures = [];

            for (let layer of layers) {
                if (options.onFeaturesFind) {
                    options.onFeaturesFind(allFeatures, false);
                }

                let source = layer.getSource();
                let featuresFromWMS = await getFeaturesFromWMS(
                    map.getView(), source, mapBrowserEvent.coordinate, layer.get('id')
                );
                if (featuresFromWMS.length > 0) {
                    allFeatures = allFeatures.concat(featuresFromWMS);
                    processed = true;
                }
            }
            if (options.onFeaturesFind) {
                options.onFeaturesFind(allFeatures, true, mapBrowserEvent.coordinate);
            }

            if (options.onShowMessage && (layers.length > 1)) {
                options.onShowMessage('');
            }
        }
    }
}

function createInteractionInfo(options) {

    let info = new Info({
        handleClickEvent: async (mapBrowserEvent) => {
            await processClick(mapBrowserEvent, options);
        }
    });

    info.set('mapToolId', 'Info');
    info.setActive(false);

    return info;
}

export default createInteractionInfo;
